import clientLookups from '@/assets/json/clientLookups.json'
import useAttachmentOverride from '@/composables/attachment_override/base.js'
import client_config from '@/assets/json/client_config.json'
import useSchema from '@/composables/useSchema.js'
import i18n from '@/i18n/i18n'
import axios from '@/services'
import { DateTime } from 'luxon'
import { notify } from 'notiwind'
import { defineStore } from 'pinia'
import { useAuthStore } from './auth'
import { useGlobalStore } from './global'
import router from '@/router'
import _ from 'lodash'
import axiosInstance from '@/services'
import { getAge } from '@/composables/useDateTime'

export const useFormStore = defineStore('form', {
  state: () => {
    return {
      // will contain the api response {steps: [], form: []}
      schema: null,

      // hosts the vee-validate form
      form: null,

      // tracks the current step of the service application
      activeStep: null,

      // list of attachments (manadatory and optional) to be expected
      attachmentsList: [],

      // draft response from API after creating/updating a service application
      draft: null,
      originalDraft: null,

      // attachments made by user
      attachments: [],
      // extra info
      extraMap: {},

      // api response by payment/pay. to be used to load the payment processor
      paymentFormData: null
    }
  },
  getters: {
    activeStepIndex() {
      return this.schema?.stepLabels?.web?.indexOf(this.activeStep)
    },
    activeForm() {
      if (!this.schema) return null
      return this.schema.steps.find((s) => s.name == this.activeStep)
    },
    applicantAttachments() {
      return this.attachmentsList.filter((att) => att.categorizeAttachment == 2)
    },
    beneficiaryAttachments() {
      return this.attachmentsList.filter(
        (att) => att.categorizeAttachment == 1 && !this.isExtraAttachment(att.code)
      )
    },
    otherAttachments() {
      return this.attachmentsList.filter(
        (att) => att.categorizeAttachment == 3 && !this.isExtraAttachment(att.code)
      )
    },
    /**
     * parsed list of available attachment types for selection
     */
    // availableAttachmentsList() {
    //   const takenTypes = this.attachments.map((att) => att.attachmentType.id)
    //   return this.attachmentsList.filter((att) => !takenTypes.includes(att.id))
    // },
    /**
     * adds support for role-based api config per schema
     * especially services located in the lib/schema/share/ directory
     *
     * returns
     * {
     *   add: {...},
     *   update: {...}
     * }
     */
    requestConfig() {
      const authStore = useAuthStore()
      const { application } = clientLookups

      let api = {}

      const hasMapping = Object.keys(this.schema.api).some((key) =>
        Object.keys(application).includes(key)
      )

      if (hasMapping) {
        const [appName, appId] = Object.entries(application).find(
          ([appName, appId]) => appId == authStore.user.applicationId
        )
        Object.assign(api, this.schema.api[appName])
      } else {
        Object.assign(api, this.schema.api)
      }

      return api
    },
    hasAttachmentsToUpload() {
      return (
        [...this.applicantAttachments, ...this.beneficiaryAttachments, ...this.otherAttachments]
          .length > 0
      )
    },
    tradeLicenseNumbers() {
      const tlNums = []

      const {
        establishmentLicenceNumber,
        delegateforCompany = {},
        delegateforCompanies = []
      } = this.draft.request

      if (delegateforCompany?.estabLicenseNumber) {
        tlNums.push(delegateforCompany?.estabLicenseNumber?.toString().match(/\d+/))
      } else if (delegateforCompanies.length > 0) {
        delegateforCompanies.forEach((dc) => {
          if (dc.estabLicenseNumber) {
            tlNums.push(dc.estabLicenseNumber.toString().match(/\d+/))
          }
        })
      } else if (establishmentLicenceNumber) {
        tlNums.push(establishmentLicenceNumber.toString().match(/\d+/))
      }

      return tlNums.toString()
    }
  },
  actions: {
    async resetForm(serviceId, needToFirst = false) {
      await useGlobalStore().startService(serviceId)
      this.schema = await useSchema(serviceId)
      if (!this.activeStep || needToFirst) this.activeStep = this.schema.stepLabels.web[0]
    },
    async getPrintUAEDocuments(isIDD, params) {
      const path = isIDD
        ? `/api/PrintUAEDocuments/getEmiratesIDDocument`
        : `/api/PrintUAEDocuments/getUAEIDDocument`
      return await axios.get(path, params)
    },
    async loadDraftFromRquestNumber(requestNumber) {
      const { add } = this.requestConfig
      // replace api add to requestNumber,sometime the string is addEidaRequest, so always replace the last part of the path
      const pathArray = add.path.split('/')
      const draftPath = add.draftPath
      pathArray[pathArray.length - 1] = draftPath || 'requestNumber'
      const path = pathArray.join('/')
      const data = await axios.get(`${path}/${requestNumber}`)
      this.draft = data
    },
    async getAttachmentList() {
      const { serviceTransactionId } = useGlobalStore().startedService

      if (serviceTransactionId == 561) {
        return await this.getCitizenEidaAttachmentList()
      }

      const path = `/api/lookup/service/${serviceTransactionId}/adminRegion/1/attachmentTypes`

      const data = await axios.get(path, { params: { v: 10.1 } })

      this.allAttachmentsList = data
      this.attachmentsList = data
    },
    async getCitizenEidaAttachmentList() {
      const path = `/api/icaRequest/draft/getCitizenEidaAttachmentTypes`
      const { requestNumber } = this.draft
      const data = await axios.get(path, { params: { requestNumber } })

      this.allAttachmentsList = data
      this.attachmentsList = data
    },
    async submitDraft(payload) {
      const { method, path } = this.requestConfig.add

      const data = await axios.request({
        url: path,
        method,
        data: payload
      })

      Object.assign(this.draft, {
        ...data,
        request: {
          ...this.draft.request,
          ...data.request
        }
      })
    },
    async updateDraft(payload) {
      const { method, path } = this.requestConfig.update

      const data = await axios.request({
        url: path,
        method,
        data: payload || this.draft
      })

      this.draft = {
        ...data,
        request: {
          ...this.draft.request,
          ...data.request
        },
        applicantInformationId: data.applicantInformationId
      }
    },
    /**
     * apply various logic
     */
    async processAttachmentList() {
      if ((this.attachmentsList || []).length === 0) return

      if (this.draft.request.hasPoliceClearanceCertificateAttachment)
        this.applyPoliceClearanceLogic()

      this.applyNationalityLogic()

      this.applyForeignerLogic()

      this.applyNewbornLogic()

      if (client_config.enableCategorizeAttachment) {
        if (
          this.applicantAttachments.length > 0 ||
          this.beneficiaryAttachments.length > 0 ||
          this.otherAttachments.length > 0
        ) {
          this.attachmentsList = [
            ...this.attachmentsList.filter((att) => att.categorizeAttachment === 1),
            ...this.attachmentsList.filter((att) => att.categorizeAttachment === 2),
            ...this.attachmentsList.filter((att) => att.categorizeAttachment === 3)
          ]
        }
      }

      const {
        params: { serviceId }
      } = router.currentRoute.value

      this.attachmentsList = await useAttachmentOverride(this.attachmentsList, serviceId)
    },

    /**
     * set logic for newborns in certain situations
     */
    applyNewbornLogic() {
      const { humanExceptionRequestTypeId } = this.draft.request
      const { IssueResidencyForNewBornInsideUAE } = clientLookups.humanExceptionRequestType

      if (humanExceptionRequestTypeId == IssueResidencyForNewBornInsideUAE) {
        this.attachmentsList.push(
          ...this.allAttachmentsList
            .filter((att) => [717, 414, 349, 172, 168].includes(att.id))
            .map((att) => ({ ...att, isMandatory: true }))
        )

        return
      }

      const excludedIds = [717, 414, 349, 172, 168]
      this.attachmentsList = this.attachmentsList.filter((p) => !excludedIds.includes(p.id))
    },

    /**
     * set logic for newborns in certain situations
     */
    applyNewbornLogic() {
      const { humanExceptionRequestTypeId } = this.draft.request
      const { IssueResidencyForNewBornInsideUAE } = clientLookups.humanExceptionRequestType

      if (humanExceptionRequestTypeId == IssueResidencyForNewBornInsideUAE) {
        this.attachmentsList.push(
          ...this.allAttachmentsList
            .filter((att) => [717, 414, 349, 172, 168].includes(att.id))
            .map((att) => ({ ...att, isMandatory: true }))
        )

        return
      }

      const excludedIds = [717, 414, 349, 172, 168]
      this.attachmentsList = this.attachmentsList.filter((p) => !excludedIds.includes(p.id))
    },

    /**
     * if the draft request has hasPoliceClearanceCertificateAttachment
     * make the attachment type certificateOfGoodConductAndBehavior mandatory
     */
    applyPoliceClearanceLogic() {
      const grabId = clientLookups.attachmentTypes.certificateOfGoodConductAndBehavior
      const gcabCertAttachmentType = this.attachmentsList.find((al) => al.id == grabId)

      if (gcabCertAttachmentType) {
        gcabCertAttachmentType.isMandatory = true
      } else {
        /**
         * basically remove this attachmentType from
         * attachmentsList & drafts
         *
         * and move it to the deletedAttachments array.
         */
        this.attachmentsList = this.attachmentsList.filter((al) => al.id != grabId)

        if ((this.draft.attachments || [])?.length > 0) {
          // if deletedAttachments is null, initialize with an empty array
          this.draft.deletedAttachments = this.draft.deletedAttachments || []
          this.draft.deletedAttachments.push(this.draft.attachments.find((al) => al.id == grabId))

          this.draft.attachments = this.draft.attachments.filter((att) => att.id != grabId)
        }
      }
    },
    setAttachmentTypeMandatory(attachmentTypeId, isMandatory = true) {
      const requiredAttachment = this.attachmentsList.find((att) => att.id == attachmentTypeId)
      if (requiredAttachment) {
        requiredAttachment.isMandatory = isMandatory
      }
    },
    removeAttachmentType(attachmentTypeId) {
      this.attachmentsList = this.attachmentsList.filter((att) => att.id != attachmentTypeId)
    },

    isExtraAttachment(attachment) {
      const { extraOptionalAttachments, supportAttachmentsForExemptions } = clientLookups
      const extraIds = [
        ...Object.values(extraOptionalAttachments),
        ...Object.values(supportAttachmentsForExemptions)
      ]

      const isExtra = extraIds.some((eId) => eId == attachment.id)

      /**
       * removes Exceptional Attachment N from the list
       * Exception Attachments have attachmentCategoryId of 2.
       * and on click of Start Service, withException is passed
       * to query params with false as its value.
       */
      const isException = attachment.attachmentCategoryId == 2
      const disabledExceptions = router.currentRoute.value?.query?.withException == false

      return isExtra || (isException && !disabledExceptions)
    },

    /**
     * set logic for certain nationalities for certain services
     * there are certain conditions for Iraqis, Iranians, Pakistanis and Afghans.
     *
     * also for saudia, kuwaiti, omani, bahraini, qatari when applying for a truck driver visa
     */
    applyNationalityLogic() {
      const { currentNationalityId } = this.draft.request
      const { iraq, iran, pakistan, afghanistan } = clientLookups.nationality

      // remove all nationality cards
      this.attachmentsList = this.attachmentsList.filter((att) => {
        const isNationalityCard = att.id < 449 || att.id > 452
        return isNationalityCard && [801, 819].includes(att.id) === false
      })

      // then only add based on currentNationalityId
      if (currentNationalityId == afghanistan) {
        this.attachmentsList.push(
          ...this.allAttachmentsList
            .filter((att) => [452, 801].includes(att.id))
            .map((att) => ({ ...att, isMandatory: true }))
        )
      }

      if (currentNationalityId == iraq) {
        this.attachmentsList.push(
          ...this.allAttachmentsList
            .filter((att) => [449].includes(att.id))
            .map((att) => ({ ...att, isMandatory: true }))
        )
      }

      if (currentNationalityId == iran) {
        this.attachmentsList.push(
          ...this.allAttachmentsList
            .filter((att) => [450].includes(att.id))
            .map((att) => ({ ...att, isMandatory: true }))
        )
      }

      if (currentNationalityId == pakistan) {
        this.attachmentsList.push(
          ...this.allAttachmentsList
            .filter((att) => [451].includes(att.id))
            .map((att) => ({ ...att, isMandatory: true }))
        )
      }

      /**
       * truck driver x nationality logic
       * basically if a person from these countries is applying as a truck driver...
       */
      const { saudi, kuwait, oman, bahrain, qatar } = clientLookups.gccNationalityId
      const {
        TruckDriversVisa30DaysSingleEntryIssue,
        TruckDriversVisa60DaysSingleEntryIssue,
        TruckDriversVisa90DaysSingleEntryIssue
      } = clientLookups.ServiceTransactions

      const isTruckDriverApplication = [
        TruckDriversVisa30DaysSingleEntryIssue,
        TruckDriversVisa60DaysSingleEntryIssue,
        TruckDriversVisa90DaysSingleEntryIssue
      ].includes(this.draft.serviceTransactionId)

      const isGccNationality = [saudi, kuwait, oman, bahrain, qatar].includes(currentNationalityId)

      if (isTruckDriverApplication && isGccNationality) {
        this.setAttachmentTypeMandatory(819)
      }
    },

    /**
     * apply attachment filter for foreigners
     */
    applyForeignerLogic() {
      const { foreignerRetiredVisa, foreignerRetiredOutsideUaeVisa } =
        clientLookups.ServiceTransactions

      const {
        propertyOwnerInTheState,
        ownerOfFinancialDepositInTheState,
        ownerOfPropertyAndTheOwnerOfDepositInTheState,
        monthlyIcomeMorethan20K
      } = clientLookups.transactionReason

      const {
        request: { transactionReasonId }
      } = this.draft
      if (this.draft.serviceTransactionId == foreignerRetiredVisa && transactionReasonId) {
        const { proofOfOwnershipOfTheProperty, proofOfFinancialDeposit, monthlyIncomeProof } =
          clientLookups.attachmentTypes

        switch (transactionReasonId) {
          case propertyOwnerInTheState: {
            this.setAttachmentTypeMandatory(proofOfOwnershipOfTheProperty, true)
            this.setAttachmentTypeMandatory(proofOfFinancialDeposit, false)
            this.setAttachmentTypeMandatory(monthlyIncomeProof, false)

            this.removeAttachmentType(monthlyIncomeProof)

            break
          }

          case ownerOfFinancialDepositInTheState: {
            this.setAttachmentTypeMandatory(proofOfOwnershipOfTheProperty, false)
            this.setAttachmentTypeMandatory(proofOfFinancialDeposit, true)
            this.setAttachmentTypeMandatory(monthlyIncomeProof, false)

            this.removeAttachmentType(monthlyIncomeProof)
            break
          }

          case ownerOfPropertyAndTheOwnerOfDepositInTheState: {
            this.setAttachmentTypeMandatory(proofOfOwnershipOfTheProperty, true)
            this.setAttachmentTypeMandatory(proofOfFinancialDeposit, true)
            this.setAttachmentTypeMandatory(monthlyIncomeProof, false)

            this.removeAttachmentType(monthlyIncomeProof)
            break
          }

          case monthlyIcomeMorethan20K: {
            this.setAttachmentTypeMandatory(proofOfOwnershipOfTheProperty, false)
            this.setAttachmentTypeMandatory(proofOfFinancialDeposit, false)
            this.setAttachmentTypeMandatory(monthlyIncomeProof, true)

            break
          }
          default:
            break
        }
      }

      if (this.draft.serviceTransactionId == foreignerRetiredOutsideUaeVisa) {
        // TODO: sort the attachmentTypes so that the additional attachments are by the end
      }
    },
    calculateAgeFromDob() {
      const dateOfBirth = this.draft?.request.dateOfBirth || this.form.values.dateOfBirth
      if (!dateOfBirth) return -1
      const possibleFormats = ["yyyy-MM-dd'T'HH:mm:ss", 'dd/MM/yyyy', 'yyyy-MM-dd']

      const findPossibleFormats = () => {
        for (const format of possibleFormats) {
          const dob = DateTime.fromFormat(dateOfBirth, format)
          if (dob.invalid) continue
          return dob
        }
      }

      const dobLxn = findPossibleFormats()
      if (!dobLxn) {
        throw new Error(`[calculateAgeFromDob] Unable to find proper format for ${dateOfBirth}`)
      }

      const diff = DateTime.now().diff(dobLxn, ['years', 'months', 'days'])
      const ageInYears = diff.years.toFixed(0)

      return ageInYears
    },
    async checkForPersonalPhotoICAO(attachment) {
      if (attachment.token) return

      const formData = new FormData()
      formData.append(0, attachment.attachmentFile)
      formData.append('attachment', attachment)

      const { requestNumber } = this.draft
      const path = `/api/draft/check/newPersonalPhoto/${requestNumber}`
      return await axios.post(path, formData, {
        params: {
          serviceTransactionId: this.draft.serviceTransactionId
        }
      })
    },
    async uploadPhoto(attachment) {
      if (attachment.token) return

      const formData = new FormData()
      formData.append(0, attachment.attachmentFile)
      formData.append('attachment', attachment)

      const { requestNumber } = this.draft
      const path = `/api/draft/check/newPersonalPhoto/${requestNumber}`
      await axios.post(path, formData, {
        params: {
          serviceTransactionId: this.draft.serviceTransactionId
        }
      })

      await axios.post('/api/draft/check/personalPhoto', formData)
    },
    validateAttachmentsList() {
      const mandatoryAttachments = this.attachmentsList.filter((att) => att.isMandatory)

      let hasUploadedAllMandatory = true

      // Check any mandatory attachment is not uploaded.
      for (let index = 0; index < mandatoryAttachments.length; index++) {
        const att = mandatoryAttachments[index]
        hasUploadedAllMandatory = this.attachments.some((e) => e.attachmentTypeId == att.id)
        if (!hasUploadedAllMandatory) {
          break
        }
      }

      if (!hasUploadedAllMandatory) {
        notify(
          {
            group: 'error',
            title: i18n.global.t('MissingTypesTitle'),
            text: i18n.global.t('MissingTypes')
          },
          6000
        )
      }

      return hasUploadedAllMandatory
    },
    async getAttachmentFromDraft() {
      this.attachments = this.draft.attachments
    },
    async uploadAttachments() {
      const globalStore = useGlobalStore()
      if (this.attachments.length <= 0) {
        return
      }

      if ((this.attachments || [])?.length > 0) {
        this.attachments[0].draftNumber = this.draft.requestNumber
      }

      await axios.post('/api/draft/attachments/updateDetail', this.attachments)

      const { kofaxAuditingAttachmebntTypes } = clientLookups
      const auditResultAttachmentType = this.attachments.find(
        (att) => att.attachmentTypeId == kofaxAuditingAttachmebntTypes
      )
      if (auditResultAttachmentType) {
        auditResultAttachmentType.autoAuditResult = null
      }

      let counter = 0
      const promises = this.attachments.map(async (attachment) => {
        if (!attachment.token) {
          const formData = new FormData()
          if (typeof attachment.attachmentFile === 'object') {
            formData.append(counter, attachment.attachmentFile)
            attachment.uploadedFileName = counter
          }
          if (attachment.attachmentTypeId !== 141) {
            attachment.serverPath = globalStore.startedService.config?.serviceConfig?.serverPath
          }

          formData.append('attachment', JSON.stringify(attachment))

          const { requestNumber } = this.draft
          const path = `/api/draft/requestNumber/${requestNumber}/attachments/upload`
          const result = await axios.post(path, formData)

          const { token, id, autoAuditResult } = result || {}
          Object.assign(attachment, {
            token,
            id,
            autoAuditResult
          })
        }

        counter += 1
      })

      await Promise.all(promises)
    },
    deleteAttachment(attachment) {
      if (!attachment) return
      this.draft.deletedAttachments = this.draft.deleteAttachments || []

      const indexToDelete = this.attachments.findIndex((att) => att == attachment)
      this.attachments.splice(indexToDelete, 1)

      // TODO: configure max file count

      if (attachment.token) {
        this.draft.deletedAttachments.push(attachment)
      }
    },
    // Get attachment file from server
    async getAttachmentFileAsBlob(id, attachmentToken, callback) {
      if (!attachmentToken) return
      const url = `/api/attachment/${id}/${attachmentToken}/view`
      const res = await axios.get(url, {
        responseType: 'blob'
      })
      return res
    },
    /**
     * removes deleted attachments from DB
     */
    async cleanupDeletedAttachments() {
      if (!this.draft.deletedAttachments) return
      const promises = this.draft.deletedAttachments.map(async (attachment) => {
        const url = `/api/draft/attachment/${attachment.token}/delete`
        await axios.get(url, {
          params: {
            attachmentId: attachment.id
          }
        })
      })
      await Promise.all(promises)
      this.draft.deleteAttachments = []

      // set attachments with autoAuditResult to this.draft.auditedAttachments
      this.draft.auditedAttachments = this.draft.auditedAttachments || []

      if (!this.draft.attachments) {
        this.draft.attachments = this.attachments
      }

      this.draft.auditedAttachments = [
        ...this.draft.attachments.filter((att) => att.autoAuditResult)
      ]
    },
    buildAttachmentsList(attachment) {
      return this.attachmentsList.filter((attachmentType) => {
        return attachment.attachmentType.id == attachmentType.id
      })
    },
    initializeDraft() {
      this.draft = Object.keys(this.draft || {}).length > 0 ? this.draft : { request: {} }
    },
    async loadAttachmentTypesByTransactionReasonId() {
      const { gccTypeCode } = this.form.values
      const transactionReasonId =
        gccTypeCode || this.extraMap.gccTypeCode || this.draft.request.gccTypeCode
      if (!transactionReasonId) return
      try {
        const res = await axios.get(`/api/lookup/service/${transactionReasonId}/attachmentTypes`)
        if (!res) return
        this.attachmentsList = this.attachmentsList
          .filter((attachment) => {
            if (attachment.id === 141 || attachment.id === 384) {
              return true
            }
            return res.some((item) => attachment.id === item.id)
          })
          .map((attachment) => ({
            ...attachment
          }))

        this.attachmentsList.map((item) => {
          if (item.id === 384) {
            item.isMandatory = false
          }
          if ([506].includes(+transactionReasonId)) {
            if ([529, 576, 141].includes(item.id)) {
              item.isMandatory = true
            } else {
              item.isMandatory = false
            }
          }
          // for families
          if ([519, 520, 521, 516, 517, 514].includes(+transactionReasonId) && +item.id !== 384) {
            item.isMandatory = true
          }
          if ([518].includes(+transactionReasonId) && +item.id !== 384) {
            if ([37, 529, 536, 141].includes(+item.id)) {
              item.isMandatory = true
            }
          }
          // for gcc guardian
          if ([685, 686, 687].includes(+transactionReasonId) && +item.id !== 384) {
            item.isMandatory = true
          }
          //for investor category
          if ([505, 507, 508].includes(+transactionReasonId) && +item.id !== 384) {
            if ([576, 529, 141].includes(+item.id)) {
              item.isMandatory = true
            }
          }
          // type craftsmen
          if (
            [497, 499, 501, 500, 489, 504, 502, 503, 496].includes(+transactionReasonId) &&
            +item.id !== 384
          ) {
            if ([536, 530, 529, 141, 531].includes(+item.id)) {
              item.isMandatory = true
            }
          }
          this.attachmentsList.sort((a, b) => b.isMandatory - a.isMandatory)
        })
      } catch (error) {
        console.error('loadAttachmentTypesByTransactionReasonId', error)
      }
    },
    async setIntegrationDetails() {
      const {
        params: { serviceId }
      } = router.currentRoute.value

      const {
        icaAndResiServiceTransactions,
        ServiceTransactions: { issueLeavePermitForExemption },
        exceptionSponsorType: { studyingInsideUAE, studyingOutsideUAE },
        proofOfStudyingCertificateAttachment: { id: proofOfStudyingCertificateAttachmentId },
        sponsorStudyStatus: { finishedUniversity, finishedSecondarySchool, studyingInUniversity },
        finishedStudyingInsideUAEAttachmentTypes: {
          certificateOfStudyUniversityCompletion,
          certificateOfContinuigStudyFromTheUniversity,
          certificateOfStudyCompletion
        }
      } = clientLookups

      const { CertificationFlag, certificationFlag, exceptionSponsorTypeId, sponsorStudyStatusId } =
        this.draft.request

      const { integrationDetails } = this.draft

      const hasAttachments = (this.attachmentsList ?? []).length > 0

      if (!hasAttachments) return

      if (icaAndResiServiceTransactions.includes(+serviceId)) {
        this.attachmentsList.filter((al) => al.token).forEach((al) => (al.hasToken = true))
      }

      if (Object.keys(this.draft?.request || {}).length === 0) return
      //  write 1879 to 1958 logic

      const certificationRequestFlag = CertificationFlag ?? certificationFlag
      if (!certificationRequestFlag && serviceId != issueLeavePermitForExemption) {
        this.attachmentsList
          .filter((att) => !!att.id && [141, 139].includes(att.id))
          .forEach((att) => (att.isMandatory = true))
      }

      if ([studyingInsideUAE, studyingOutsideUAE].includes(exceptionSponsorTypeId)) {
        this.attachmentsList
          .filter((att) => !!att.id && att.id == proofOfStudyingCertificateAttachmentId)
          .forEach((att) => (att.isMandatory = true))
      }

      if (sponsorStudyStatusId == finishedUniversity) {
        const nonMandatoryCriteria = [
          certificateOfContinuigStudyFromTheUniversity,
          certificateOfStudyCompletion
        ]

        this.attachmentsList.forEach((att) => {
          if (att.id == certificateOfStudyUniversityCompletion) {
            att.isMandatory = true
          }

          if (nonMandatoryCriteria.includes(att.id)) {
            att.isMandatory = false
          }
        })
      }

      if (sponsorStudyStatusId == finishedSecondarySchool) {
        const nonMandatoryCriteria = [
          certificateOfStudyUniversityCompletion,
          certificateOfContinuigStudyFromTheUniversity
        ]

        this.attachmentsList.forEach((att) => {
          if (att.id == certificateOfStudyCompletion) {
            att.isMandatory = true
          }

          if (nonMandatoryCriteria.includes(att.id)) {
            att.isMandatory = false
          }
        })
      }

      if (sponsorStudyStatusId == studyingInUniversity) {
        const nonMandatoryCriteria = [
          certificateOfStudyUniversityCompletion,
          certificateOfStudyCompletion
        ]

        this.attachmentsList.forEach((att) => {
          if (att.id == certificateOfContinuigStudyFromTheUniversity) {
            att.isMandatory = true
          }

          if (nonMandatoryCriteria.includes(att.id)) {
            att.isMandatory = false
          }
        })
      }

      // write 1960 to 2000 logic
      const promises = []
      this.attachmentsList
        .filter((att) => att.hasIntegration)
        .forEach((att) => {
          let isNoDataFound = false
          let requestBody

          att.integrationDetailsFound = false

          const integrationDetail = (integrationDetails ?? []).find(
            (integ) => integ.requestAttachmentId == att.id
          )

          if (integrationDetail?.result == 'NO_DATA_FOUND') {
            isNoDataFound = true
            att.integrationDetailsFound = true
          }

          requestBody = integrationDetail?.requestBody

          /**
           * i don't know where scope.isUnifiedForm is set
           * same goes for scope.hasIntegration
           * if (scope.isUnifiedForm != true || (scope.isUnifiedForm == true && scope.hasIntegration == true)) {
           */

          if (
            (!att.integrationDetailsFound && !isNoDataFound) ||
            (requestBody && this.checkParametersChanged(att, requestBody))
          ) {
            promises.push(this.getIntegrationDetails(att))
          }
        })

      await Promise.all(promises)
    },
    checkParametersChanged(att, requestBody = {}) {
      if (Object.keys(requestBody).length === 0) return false

      const newParams = {
        EmiratesIDNumber: null,
        MOIUnifiedNumber: null,
        VisaResidenceNumber: null,
        FitnessCertificateNumber: null,
        TradeLicenseNumber: null,
        TradeLicenseNumbers: null,
        FileServiceCode: null,
        OwnerCode: null,
        FileYear: null,
        FileDepartmentCode: null,
        FileSequanceNumber: null
      }

      const { id: attachmentTypeId } = att

      const {
        sponsorNumber,
        resiDepartmentCode,
        resiYear,
        resiServiceCode,
        resiSequenceNumber,
        personUnifiedNumber,
        identityNumber,
        serviceCode,
        serviceYear,
        departmentCode,
        sequenceNumber,
        applicantIdentityNumber,
        sponsorDepartmentId,
        immigrationDepartmentId
      } = this.draft.request

      if ([190, 102, 800, 358].includes(attachmentTypeId)) {
        newParams.MOIUnifiedNumber = sponsorNumber
      } else if ([122, 49, 50, 24, 338].includes(attachmentTypeId)) {
        if (resiDepartmentCode) {
          const fileNumber = [resiDepartmentCode, resiYear, resiServiceCode, resiSequenceNumber]

          Object.assign(newParams, {
            EmiratesIDNumber: identityNumber,
            MOIUnifiedNumber: personUnifiedNumber,
            VisaResidenceNumber: fileNumber.join('/')
          })
        }
      } else if ([45].includes(attachmentTypeId)) {
        Object.assign(newParams, {
          MOIUnifiedNumber: personUnifiedNumber,
          FileServiceCode: serviceCode,
          FileYear: serviceYear,
          FileDepartmentCode: departmentCode,
          FileSequanceNumber: sequenceNumber
        })
      } else if ([268, 301].includes(attachmentTypeId)) {
        Object.assign(newParams, {
          EmiratesIDNumber: applicantIdentityNumber,
          MOIUnifiedNumber: sponsorNumber
        })
      } else if ([189].includes(attachmentTypeId)) {
        Object.assign(newParams, {
          EmiratesIDNumber: applicantIdentityNumber
        })
      } else if ([73, 245, 790].includes(attachmentTypeId)) {
        Object.assign(newParams, {
          EmiratesIDNumber: identityNumber,
          MOIUnifiedNumber: personUnifiedNumber
        })
      } else if ([17, 144, 150, 18, 226].includes(attachmentTypeId)) {
        Object.assign(newParams, {
          MOIUnifiedNumber: sponsorNumber,
          TradeLicenseNumbers: this.tradeLicenseNumbers,
          DepartmentId: sponsorDepartmentId ?? immigrationDepartmentId
        })
      } else if ([149].includes(attachmentTypeId)) {
        return true
      }

      if (Object.keys(requestBody).length > 0 && Object.keys(newParams).length > 0) {
        return _.isEqual(requestBody, newParams)
      } else return false
    },
    isAdministrativeRegionAllowed(att) {
      const authStore = useAuthStore()
      const { applicationId } = authStore.user
      const { administrativeRegionId } = this.draft

      const {
        adminRegions: { abuDhabi, ain, dhafra }
      } = clientLookups

      const { useAttachmentIntegrationDetailsFromDBConfigKey } = client_config

      if (!att) {
        return [abuDhabi, ain, dhafra].includes(administrativeRegionId)
      }

      const {
        integrationAdminReagionsIds = [],
        lookupInfoSourceId,
        id: attachmentId,
        integrationApplicationTypesIds
      } = att

      if (
        (integrationAdminReagionsIds ?? []).length > 0 &&
        lookupInfoSourceId &&
        useAttachmentIntegrationDetailsFromDBConfigKey
      ) {
        const userApplicationId = this.draft.applicationId ?? applicationId

        /**
         * yup. im also unable to find enableAutoUpload.
         * so this block of code will not execute
         */
        // if (lookups.lookupInfoSourceIdsForApplicant.filter(e => e === attachmentType.lookupInfoSourceId).length > 0
        //   || lookups.lookupInfoSourceIdsForBenficiary.filter(e => e === attachmentType.lookupInfoSourceId).length > 0
        //   || lookups.lookupInfoSourceIdsForMother.filter(e => e === attachmentType.lookupInfoSourceId).length > 0
        //   || lookups.lookupInfoSourceIdsForVisa.filter(e => e === attachmentType.lookupInfoSourceId).length > 0) {

        //   if (scope.enableAutoUploadAttachment(attachmentType.lookupInfoSourceId) || window.config.enableDisableAutoLoaderForAttachments == false) {
        //       return false;
        //   }
        // }

        if (
          integrationAdminReagionsIds.includes(administrativeRegionId) &&
          (integrationApplicationTypesIds ?? []).includes(userApplicationId) === false
        ) {
          return false
        }

        return integrationAdminReagionsIds.includes(administrativeRegionId)
      } else {
        const inRegion = [1, 8, 9, 4].includes(administrativeRegionId)
        const medicalFitnessInRegion =
          [122, 49, 50, 24, 338, 806].includes(attachmentId) &&
          [3, 4, 5, 6, 7].includes(administrativeRegionId)
        const tawtheeqInRegion =
          [190, 102, 800, 358].includes(attachmentId) &&
          [3, 4, 5, 6].includes(administrativeRegionId)
        const tradeLicenseInSharjah =
          [17, 144, 149, 150, 18, 226].includes(attachmentId) &&
          [3, 6].includes(administrativeRegionId)
        const healthInsurance = [73, 245, 55, 790].includes(attachmentId)
        const otherAttachmentTypes = [994, 992].includes(attachmentId)

        return (
          inRegion ||
          medicalFitnessInRegion ||
          tawtheeqInRegion ||
          tradeLicenseInSharjah ||
          healthInsurance ||
          otherAttachmentTypes
        )
      }
    },
    async getIntegrationDetails(att) {
      const {
        attachmentInfoSource: {
          departmentOfHealthAbuDhabiHealthFitnessCertificate,
          abuDhabiMunicipalityTawtheeqContract,
          departmentOfEconomicDevelopmentAbuDhabiCommercialLicense,
          AbuDhabiPoliceOwnershipOfTheVehicle,
          AbuDhabiWaterAndElectricityAuthorityElectricitybill,
          EmiratesIDAuthorityApplicationForID,
          MinistryOfHealthHealthFitnessCertificate,
          IntegrationWithMOFA,
          MinistryOfInteriorUAE
        },
        ServiceTransactions: { issueBlueResidencyAndIdentityForNewBornInsideUAE = null }
      } = clientLookups

      const {
        exceptionalServicesForAttachmentIntegrationList,
        useAttachmentIntegrationDetailsFromDB,
        enableDisableAutoLoaderForAttachments = false,
        autoUploadAttachmentInfoSourceIds
      } = client_config

      const {
        params: { serviceId }
      } = router.currentRoute.value

      const isExceptionalServicesForAttachmentIntegrationList =
        exceptionalServicesForAttachmentIntegrationList.includes(serviceId)

      if (
        !this.isAdministrativeRegionAllowed(att) &&
        !isExceptionalServicesForAttachmentIntegrationList
      )
        return

      const { integrationAdminReagionsIds = [], lookupInfoSourceId } = att

      const {
        id: draftId,
        administrativeRegionId,
        request: {
          applicantIdentityNumber,
          sponsorNumber,
          sponsorDepartmentId,
          immigrationDepartmentId,
          personUnifiedNumber,
          dateOfBirth,
          resiDepartmentCode,
          resiYear,
          resiServiceCode,
          resiSequenceNumber,
          resiVisaDepartmentCode,
          resiVisaYear,
          resiVisaServiceCode,
          resiVisaSequenceNumber,
          serviceCode,
          serviceYear,
          departmentCode,
          sequenceNumber,
          passportNumber,
          passportTypeId,
          passportCountryId,
          damanInsuranceCardNumber,
          currentNationalityId,
          birthCertificateNumber,
          identityNumber,
          currentVisaRequestNumber
        }
      } = this.draft

      const baseQuery = {
        draftId
      }

      /**
       * New approach get attachment integration details from DB
       * lookupInfoSourceId, integrationApplicationTypesIds, integrationAdminReagionsIds
       */
      if (
        integrationAdminReagionsIds?.length > 0 &&
        lookupInfoSourceId &&
        useAttachmentIntegrationDetailsFromDB
      ) {
        switch (lookupInfoSourceId) {
          case abuDhabiMunicipalityTawtheeqContract: {
            const response = await axiosInstance.get(
              '/api/documentIntegration/information/tawtheeq',
              {
                params: {
                  ...baseQuery,
                  unifiedNumber: sponsorNumber,
                  administrativeRegionId
                }
              }
            )

            await this.handleIntegrationResult(response)
            break
          }

          case departmentOfHealthAbuDhabiHealthFitnessCertificate: {
            break
          }

          case EmiratesIDAuthorityApplicationForID:
            break

          case MinistryOfInteriorUAE:
            break

          case AbuDhabiPoliceOwnershipOfTheVehicle:
            break
          case AbuDhabiWaterAndElectricityAuthorityElectricitybill:
            break
          case MinistryOfHealthHealthFitnessCertificate:
            break
          case departmentOfEconomicDevelopmentAbuDhabiCommercialLicense:
            break
          case IntegrationWithMOFA:
            break
          default:
            break
        }

        /**
         * yup. im also unable to find enableAutoUpload.
         * so this block of code will not execute
         */
        // if (!scope.enableAutoUploadAttachment(attachmentType.lookupInfoSourceId) && (window.config.enableDisableAutoLoaderForAttachments == true)
        //     && (lookups.lookupInfoSourceIdsForApplicant.filter(e => e === attachmentType.lookupInfoSourceId).length > 0
        //         || lookups.lookupInfoSourceIdsForBenficiary.filter(e => e === attachmentType.lookupInfoSourceId).length > 0
        //         || lookups.lookupInfoSourceIdsForMother.filter(e => e === attachmentType.lookupInfoSourceId).length > 0
        //         || lookups.lookupInfoSourceIdsForVisa.filter(e => e === attachmentType.lookupInfoSourceId).length > 0)) {

        //     if (scope.requestDraft?.request?.identityNumber == null && lookups.lookupInfoSourceIdsForMother.filter(e => e === attachmentType.lookupInfoSourceId).length > 0) {
        //         scope.requestDraft.request.identityNumber = scope.requestDraft?.request?.motherEID;
        //     }

        //     apiHelperService.get('PrintUAEDocuments/getEmiratesDocumentAutoLoader?draftId=' + scope.requestDraft.id + '&lookupInfoSourceId=' + attachmentType.lookupInfoSourceId + '&beneficiaryIdentityNumber=' + (scope.requestDraft.request.identityNumber ? scope.requestDraft.request.identityNumber : "") + '&personUnifiedNumber=' + (scope.requestDraft.request.personUnifiedNumber ? scope.requestDraft.request.personUnifiedNumber : "") + '&motherUnifiedNumber=' + (scope.requestDraft.request.motherUnifiedNumber ? scope.requestDraft.request.motherUnifiedNumber : ""), options);
        // }
      } else {
        // old approach
        if ([190, 102, 800, 358].includes(att.id)) {
          const response = await axiosInstance.get(
            '/api/documentIntegration/information/tawtheeq',
            {
              params: {
                ...baseQuery,
                sponsorNumber,
                administrativeRegionId
              }
            }
          )
          await this.handleIntegrationResult(response, att)
        } else if ([122, 49, 50, 24, 338].includes(att.id)) {
          const age = getAge(dateOfBirth)
          if (age >= 18) {
            const fileNumberParts = {
              departmentCode: resiDepartmentCode,
              year: resiYear,
              serviceCode: resiServiceCode,
              sequenceNumber: resiSequenceNumber
            }

            if (!resiDepartmentCode) {
              Object.assign(fileNumberParts, {
                departmentCode: resiVisaDepartmentCode,
                year: resiVisaYear,
                serviceCode: resiVisaServiceCode,
                sequenceNumber: resiVisaSequenceNumber
              })
            }

            const params = {
              ...baseQuery,
              unifiedNumber: personUnifiedNumber,
              administrativeRegionId,
              fileServiceCode: fileNumberParts.serviceCode,
              fileYear: fileNumberParts.year,
              fileDepartmentCode: fileNumberParts.departmentCode,
              fileSequanceNumber: fileNumberParts.sequenceNumber,
              serviceTransactionId: serviceId
            }

            if (resiDepartmentCode) {
              const { departmentCode, year, serviceCode, sequenceNumber } = fileNumberParts
              params.fileNumber = [departmentCode, year, serviceCode, sequenceNumber].join('/')
            }

            const response = await axiosInstance.get('/documentIntegration/information/haad', {
              params
            })
            await this.handleIntegrationResult(response)
          } else {
            Object.assign(att, {
              loadIntegrationDetails: false,
              integrationDetailsFound: false,
              integrationDetailsNoFound: true,
              integrationDetailsLoadingFinished: true,
              isMandatory: false
            })

            if (serviceId != issueBlueResidencyAndIdentityForNewBornInsideUAE) {
              notify(
                {
                  group: 'info',
                  text: i18n.global.t('mdicalExamResultAgeUnder18')
                },
                6000
              )
            }
          }
        } else if ([45, 339, 70, 814].includes(att.id)) {
          const fileNumberParts = {
            departmentCode: departmentCode,
            year: serviceYear,
            serviceCode: serviceCode,
            sequenceNumber: sequenceNumber
          }

          if (!departmentCode) {
            Object.assign(fileNumberParts, {
              departmentCode: resiVisaDepartmentCode,
              year: resiVisaYear,
              serviceCode: resiVisaServiceCode,
              sequenceNumber: resiVisaSequenceNumber
            })
          }

          const response = await axiosInstance.get('/api/documentIntegration/information/eida', {
            params: {
              ...baseQuery,
              eIDAApplicationNumber: '',
              unifiedNumber: personUnifiedNumber,
              fileServiceCode: fileNumberParts.serviceCode,
              fileYear: fileNumberParts.year,
              fileDepartmentCode: fileNumberParts.departmentCode,
              fileSequanceNumber: fileNumberParts.sequenceNumber,
              passportNumber,
              passportTypeId,
              passportCountryId
            }
          })

          await this.handleIntegrationResult(response, att)
        } else if ([268, 301].includes(att.id)) {
          const response = await axiosInstance.get(
            '/api/documentIntegration/information/vehicleownership',
            {
              params: {
                ...baseQuery,
                emirateIdentityNumber: applicantIdentityNumber,
                unifiedNumber: sponsorNumber
              }
            }
          )

          await this.handleIntegrationResult(response, att)
        } else if ([189].includes(att.id)) {
          const response = await axiosInstance.get(
            '/api/documentIntegration/information/waterelectricity',
            {
              params: {
                ...baseQuery,
                emirateIdentityNumber: applicantIdentityNumber,
                administrativeRegionId
              }
            }
          )

          await this.handleIntegrationResult(response, att)
        } else if ([73, 245, 55, 790].includes(att.id)) {
          const response = await axiosInstance.get(
            '/api/documentIntegration/information/healthinsurance',
            {
              params: {
                ...baseQuery,
                emirateIdentityNumber: applicantIdentityNumber,
                unifiedNumber: personUnifiedNumber,
                damanInsuranceCardNumber: damanInsuranceCardNumber,
                passportNumber,
                nationalityId: currentNationalityId,
                birthCertificateNumber
              }
            }
          )

          await this.handleIntegrationResult(response, att)
        } else if ([17, 144, 149, 150, 18, 226].includes(att.id)) {
          const response = await axiosInstance.get(
            '/api/documentIntegration/information/tradelicense',
            {
              params: {
                ...baseQuery,
                unifiedNumber: sponsorNumber,
                tradeLicenseNumbers: this.tradeLicenseNumbers,
                departmentId: sponsorDepartmentId ?? immigrationDepartmentId
              }
            }
          )

          await this.handleIntegrationResult(response, att)
        } else if ([806].includes(att.id)) {
          const response = await axiosInstance.get(
            '/api/documentIntegration/information/MofaNOCRequest',
            {
              params: {
                ...baseQuery
              }
            }
          )

          await this.handleIntegrationResult(response, att)
        } else if ([986, 987].includes(att.id)) {
          const response = await axiosInstance.get(
            '/api/documentIntegration/information/educationDocuments',
            {
              params: {
                ...baseQuery,
                identityNumber,
                administrativeRegionId,
                requestAttachmentId: att.id
              }
            }
          )

          await this.handleIntegrationResult(response, att)
        } else if ([988].includes(att.id)) {
          const response = await axiosInstance.get(
            '/api/documentIntegration/information/marriageCertificateDocuments',
            {
              params: {
                ...baseQuery,
                identityNumber,
                unifiedNumber: personUnifiedNumber,
                serviceTransactionId: serviceId,
                administrativeRegionId,
                requestAttachmentId: att.id
              }
            }
          )

          await this.handleIntegrationResult(response, att)
        } else if ([989].includes(att.id)) {
          const response = await axiosInstance.get(
            '/api/documentIntegration/information/birthCertificateDocuments',
            {
              params: {
                ...baseQuery,
                identityNumber,
                unifiedNumber: personUnifiedNumber,
                serviceTransactionId: serviceId,
                administrativeRegionId,
                requestAttachmentId: att.id
              }
            }
          )

          await this.handleIntegrationResult(response, att)
        } else if ([992, 994].includes(att.id)) {
          const fileNumberParts = {
            departmentCode: departmentCode,
            year: serviceYear,
            serviceCode: serviceCode,
            sequenceNumber: sequenceNumber
          }

          if (!departmentCode) {
            Object.assign(fileNumberParts, {
              departmentCode: resiVisaDepartmentCode,
              year: resiVisaYear,
              serviceCode: resiVisaServiceCode,
              sequenceNumber: resiVisaSequenceNumber
            })
          }

          const response = await axiosInstance.get(
            '/api/documentIntegration/information/visaDocument',
            {
              params: {
                ...baseQuery,
                requestAttachmentId: att.id,
                currentVisaRequestNumber,
                serviceTransactionId: serviceId,
                departmentCode: fileNumberParts.departmentCode,
                serviceYear: fileNumberParts.year,
                serviceCode: fileNumberParts.serviceCode,
                sequenceNumber: fileNumberParts.sequenceNumber
              }
            }
          )

          await this.handleIntegrationResult(response, att)
        } else if ([350, 351].includes(att.id)) {
          const response = await axiosInstance.get(
            '/api/documentIntegration/information/emiratesIdCopyAsXml',
            {
              params: {
                ...baseQuery,
                unifiedNumber: personUnifiedNumber,
                serviceTransactionId: serviceId,
                administrativeRegionId,
                requestAttachmentId: att.id
              }
            }
          )

          await this.handleIntegrationResult(response, att)
        } else if ([774, 775].includes(att.id)) {
          const response = await axiosInstance.get(
            '/api/documentIntegration/information/emiratesIdCopyAsXml',
            {
              params: {
                ...baseQuery,
                unifiedNumber: sponsorNumber,
                serviceTransactionId: serviceId,
                administrativeRegionId,
                requestAttachmentId: att.id
              }
            }
          )

          await this.handleIntegrationResult(response, att)
        }

        /**
         * yup. im also unable to find enableAutoUpload.
         * so this block of code will not execute
         */
        // if (!scope.enableAutoUploadAttachment(attachmentType.lookupInfoSourceId) && (window.config.enableDisableAutoLoaderForAttachments == true)
        //   && (lookups.lookupInfoSourceIdsForApplicant.filter(e => e === attachmentType.lookupInfoSourceId).length > 0
        //       || lookups.lookupInfoSourceIdsForBenficiary.filter(e => e === attachmentType.lookupInfoSourceId).length > 0
        //       || lookups.lookupInfoSourceIdsForMother.filter(e => e === attachmentType.lookupInfoSourceId).length > 0
        //       || lookups.lookupInfoSourceIdsForVisa.filter(e => e === attachmentType.lookupInfoSourceId).length > 0)) {

        //   if (scope.requestDraft?.request?.identityNumber == null && lookups.lookupInfoSourceIdsForMother.filter(e => e === attachmentType.lookupInfoSourceId).length > 0) {
        //       scope.requestDraft.request.identityNumber = scope.requestDraft?.request?.motherEID;
        //   }

        //   apiHelperService.get('PrintUAEDocuments/getEmiratesDocumentAutoLoader?draftId=' + scope.requestDraft.id + '&lookupInfoSourceId=' + attachmentType.lookupInfoSourceId + '&beneficiaryIdentityNumber=' + (scope.requestDraft.request.identityNumber ? scope.requestDraft.request.identityNumber : "") + '&personUnifiedNumber=' + (scope.requestDraft.request.personUnifiedNumber ? scope.requestDraft.request.personUnifiedNumber : "") + '&motherUnifiedNumber=' + (scope.requestDraft.request.motherUnifiedNumber ? scope.requestDraft.request.motherUnifiedNumber : ""), options);

        // }
      }
    },
    async handleIntegrationResult(response, att) {
      const {
        params: { serviceId }
      } = router.currentRoute.value

      const {
        medicalHealthExamExpiryDateLable,
        medicalHealthExamResultLable,
        medicalHealthExamResultCode: { fit, underProcess, expired, noDataFound, fitOneYear, unfit },
        sponsorTypeId: sponsorTypeIds,
        familyMemberServiceId: familyMemberServiceIds,
        maritalStatusId: maritalStatusIds
      } = clientLookups
      const { AllowDynamicIntegrationImageGeneration } = client_config
      const {
        integrationDetails = [],
        request: { dateOfBirth, sponsorTypeId, maritalStatusId }
      } = this.draft
      const { id: attachmentId, resultDataNode } = att

      const isExpired = (date) => {
        const now = DateTime.now()
        const formatted = DateTime.fromJSDate(date)
        return formatted < now
      }

      const replaceOrAddIntegrationDetail = () => {
        const { requestAttachmentId } = response

        const foundIntegrationDetail = integrationDetails.find(
          (integDet) => integDet.requestAttachmentId == requestAttachmentId
        )

        if (foundIntegrationDetail) {
          foundIntegrationDetail.integrationDetails = response
          return
        }

        this.draft.integrationDetails.push(response)
      }

      if (AllowDynamicIntegrationImageGeneration) {
        // handle me
      }

      att.loadIntegrationDetails = false

      this.draft.request.healthInsuranceUploadedFromIntegration =
        [73, 245, 55, 790].includes(attachmentId) && (resultDataNode?.nodes ?? []).length > 0

      if ((resultDataNode?.nodes ?? []).length > 0) {
        Object.assign(att, {
          integrationDetailsFound: true,
          integrationDetailsNoFound: false
        })

        replaceOrAddIntegrationDetail()

        // medical exam attachment
        if ([122, 49, 50, 24, 338].includes(attachmentId)) {
          const age = getAge(dateOfBirth)
          if (age >= 18) {
            this.draft.integrationDetails
              .filter((x) => x.requestAttachmentId == attachmentId)
              .forEach((integDet) => {
                if (integDet.result == 'NO_DATA_FOUND') {
                  att.integrationDetailsFound = false
                  notify(
                    {
                      group: 'error',
                      text: i18n.global.t('mdicalExamResultNoDataFound')
                    },
                    6000
                  )
                  return
                }

                att.integrationDetailsFound = true
                const {
                  resultDataNode: { nodes = [] }
                } = integDet

                nodes.forEach((node) => {
                  const { lableCode } = node

                  if (lableCode == medicalHealthExamExpiryDateLable && isExpired(node.value)) {
                    notify(
                      {
                        group: 'error',
                        text: i18n.global.t('mdicalExamResultExpired')
                      },
                      6000
                    )

                    return
                  }

                  if (lableCode == medicalHealthExamResultLable) {
                    const examResultCode = node.value

                    if ([fit].includes(examResultCode)) {
                      att.isMandatory = false
                      notify(
                        {
                          group: 'success',
                          text: i18n.global.t('mdicalExamResultFit')
                        },
                        6000
                      )
                      return
                    } else if ([underProcess, expired, noDataFound].includes(examResultCode)) {
                      att.isMandatory = true
                      notify(
                        {
                          group: 'warning',
                          text: i18n.global.t('mdicalExamResultUnderProcess')
                        },
                        6000
                      )
                      return
                    } else if ([fitOneYear].includes(examResultCode)) {
                      att.isMandatory = false
                      notify(
                        {
                          group: 'warning',
                          text: i18n.global.t('mdicalExamResultFitOneYear')
                        },
                        6000
                      )
                      return
                    } else if (
                      [unfit].includes(examResultCode) &&
                      sponsorTypeIds.includes(sponsorTypeId) &&
                      familyMemberServiceIds.includes(serviceId) &&
                      maritalStatusIds.includes(maritalStatusId)
                    ) {
                      att.isMandatory = false
                    } else if ([unfit].includes(examResultCode)) {
                      att.isMandatory = true
                      notify(
                        {
                          group: 'error',
                          text: i18n.global.t('mdicalExamResultUnfit')
                        },
                        6000
                      )
                      return
                    }
                  }
                })
              })
          } else {
            att.isMandatory = false
            notify(
              {
                group: 'info',
                text: i18n.global.t('mdicalExamResultAgeUnder18')
              },
              6000
            )
          }
        }
      } else {
        Object.assign(att, {
          integrationDetailsFound: false,
          integrationDetailsNoFound: true
        })
      }

      if (response?.result == 'NO_DATA_FOUND') {
        if ([122, 49, 50, 24, 338].includes(attachmentId)) {
          const age = getAge(dateOfBirth)
          if (age >= 18) {
            att.isMandatory = true
            notify(
              {
                group: 'error',
                text: i18n.global.t('mdicalExamResultNoDataFound')
              },
              6000
            )
          } else {
            att.isMandatory = false
            notify(
              {
                group: 'info',
                text: i18n.global.t('mdicalExamResultAgeUnder18')
              },
              6000
            )
          }
        }

        if ([73, 245, 55, 790].includes(attachmentId)) {
          att.isMandatory = true
        }
      }

      if (response?.result == 'NOT_ALLOWED_ADMIN_REGION_FOR_HEALTH_INSURANCE') {
        att.loadIntegrationDetails = false
        att.integrationDetailsNoFound = false
      }
    }
  },
  persist: {
    storage: sessionStorage
  }
})
